import { useEffect } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import "leaflet.fullscreen/Control.FullScreen.css";
import "./FullscreenControlCustom.css";

interface FullscreenControlCustomProps {
  position?: L.ControlPosition;
  title?: string;
  titleCancel?: string;
}

const FullscreenControlCustom: React.FC<FullscreenControlCustomProps> = ({
  position = "topright",
  title = "Plein ecran",
  titleCancel = "Quitter le plein ecran",
}) => {
  const map = useMap();

  useEffect(() => {
    const container = map?.getContainer();
    if (!map || !container) return;

    const FullscreenControl = L.Control.extend({
      options: { position },

      onAdd() {
        const wrapper = L.DomUtil.create(
          "div",
          "leaflet-control-fullscreen leaflet-bar leaflet-control"
        );
        const button = L.DomUtil.create(
          "a",
          "leaflet-control-fullscreen-button geotrackin-fullscreen-button",
          wrapper
        ) as HTMLAnchorElement;

        button.href = "#";
        button.role = "button";

        const updateTitle = () => {
          const isFullscreen = document.fullscreenElement === container;
          const nextTitle = isFullscreen ? titleCancel : title;
          button.title = nextTitle;
          button.setAttribute("aria-label", nextTitle);
        };

        const toggleFullscreen = async (event: Event) => {
          L.DomEvent.stop(event);

          try {
            if (document.fullscreenElement === container) {
              await document.exitFullscreen?.();
            } else {
              await container.requestFullscreen?.();
            }
          } catch (error) {
            console.warn("Unable to toggle fullscreen:", error);
          } finally {
            updateTitle();
            window.setTimeout(() => map.invalidateSize(), 150);
          }
        };

        L.DomEvent.disableClickPropagation(wrapper);
        L.DomEvent.on(button, "click", toggleFullscreen);
        document.addEventListener("fullscreenchange", updateTitle);

        (wrapper as any)._cleanupFullscreen = () => {
          L.DomEvent.off(button, "click", toggleFullscreen);
          document.removeEventListener("fullscreenchange", updateTitle);
        };

        updateTitle();
        return wrapper;
      },

      onRemove(controlMap: L.Map) {
        const controlContainer = (this as unknown as L.Control).getContainer?.() as
          | (HTMLDivElement & { _cleanupFullscreen?: () => void })
          | undefined;
        controlContainer?._cleanupFullscreen?.();
        controlMap.invalidateSize();
      },
    });

    const control = new FullscreenControl();
    control.addTo(map);

    const handleFullscreenChange = () => {
      window.setTimeout(() => map.invalidateSize(), 150);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      map.removeControl(control);
    };
  }, [map, position, title, titleCancel]);

  return null;
};

export default FullscreenControlCustom;
